import React, { useMemo } from 'react';

/**
 * 탭 항목: 문자열 또는 { label, icon?, disabled? }
 * @typedef {string | { label: React.ReactNode, icon?: React.ReactNode, disabled?: boolean }} TabItem
 */

/**
 * @param {TabItem[]} tabs
 * @param {number} activeTab
 * @param {Function} onChange
 * @param {string} className
 * @param {'default' | 'pill' | 'segmented'} variant
 * @param {string} activeColor - variant='pill'일 때 활성 탭 배경색 (기본 var(--theme-color-primary))
 */
const Tabs = ({ tabs, activeTab, onChange, className = '', variant = 'default', activeColor = 'var(--theme-color-primary)' }) => {
  const isPill = variant === 'pill';
  const isSegmented = variant === 'segmented';

  const normalizedTabs = useMemo(
    () =>
      (tabs || []).map((tab) => {
        if (tab != null && typeof tab === 'object' && !Array.isArray(tab) && Object.prototype.hasOwnProperty.call(tab, 'label')) {
          return {
            label: tab.label,
            icon: tab.icon ?? null,
            disabled: Boolean(tab.disabled),
          };
        }
        return { label: tab, icon: null, disabled: false };
      }),
    [tabs],
  );

  const containerClass = [
    'tabs-container',
    isPill ? 'tabs-container--pill' : '',
    isSegmented ? 'tabs-container--segmented' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const pillStyle = isPill ? { '--tabs-pill-active-bg': activeColor } : undefined;

  return (
    <>
      <div className={containerClass} style={pillStyle}>
        {normalizedTabs.map((item, index) => (
          <button
            key={index}
            type="button"
            className={`tab-button ${activeTab === index ? 'active' : ''}`}
            disabled={item.disabled}
            onClick={() => !item.disabled && onChange(index)}
          >
            {item.icon ? (
              <span className="tabs-tab-icon" aria-hidden>
                {item.icon}
              </span>
            ) : null}
            <span className="tabs-tab-label">{item.label}</span>
          </button>
        ))}
      </div>

      <style>{`
        .tabs-container {
          display: flex;
          flex-wrap: wrap;
          gap: 24px;
          margin-bottom: 20px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
          position: relative;
        }

        .tab-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          border: none;
          background: transparent;
          color: #64748b;
          padding: 4px 0;
          border-radius: 0;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          position: relative;
          transition: color 0.2s ease;
          line-height: 1.4;
        }

        .tabs-container:not(.tabs-container--segmented):not(.tabs-container--pill) .tab-button .tabs-tab-icon {
          margin-right: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .tab-button:hover:not(:disabled) {
          color: #475569;
        }

        .tab-button:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .tab-button.active {
          background: transparent;
          border: none;
          color: #9333ea;
          font-weight: 600;
        }

        .tab-button.active::after {
          content: '';
          position: absolute;
          bottom: -8px;
          left: 0;
          right: 0;
          height: 2px;
          background: #9333ea;
        }

        /* variant: pill */
        .tabs-container--pill {
          gap: 0;
          margin-bottom: 24px;
          padding: 0;
          border: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          border-radius: 10px;
          background: #fff;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
          overflow: hidden;
        }

        .tabs-container--pill .tab-button {
          flex: 1;
          min-width: 0;
          padding: 12px 24px;
          font-size: 15px;
          font-weight: 600;
          color: #6b7280;
          border-radius: 0;
          transition: background 0.2s ease, color 0.2s ease;
        }

        .tabs-container--pill .tab-button .tabs-tab-icon {
          margin-right: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .tabs-container--pill .tab-button:hover:not(:disabled) {
          color: #475569;
          background: rgba(0,0,0,0.04);
        }

        .tabs-container--pill .tab-button.active {
          color: #fff;
          background: var(--theme-color-primary, #7c3aed);
        }

        .tabs-container--pill .tab-button.active::after {
          display: none;
        }

        .tabs-container--pill .tab-button:first-child {
          border-top-left-radius: 10px;
          border-bottom-left-radius: 10px;
        }
        .tabs-container--pill .tab-button:last-child {
          border-top-right-radius: 10px;
          border-bottom-right-radius: 10px;
        }

        /* variant: segmented (iOS-style; 너비는 내용만큼만 — 기본 탭과 같이 컴팩트) */
        .tabs-container--segmented {
          flex-wrap: nowrap;
          align-self: flex-start;
          width: fit-content;
          max-width: 100%;
          gap: 4px;
          margin-bottom: 20px;
          padding: 5px 10px;
          border: none;
          border-bottom: none;
          border-radius: 9999px;
          background: #f2f2f7;
          box-shadow: none;
          overflow-x: auto;
          overflow-y: hidden;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
        }

        .tabs-container--segmented::-webkit-scrollbar {
          display: none;
        }

        .tabs-container--segmented .tab-button {
          flex: 0 0 auto;
          min-width: 0;
          padding: 8px 20px;
          border-radius: 9999px;
          font-size: 14px;
          font-weight: 500;
          color: #0a0a0a;
          background: transparent;
          box-shadow: none;
          white-space: nowrap;
          transition: background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
        }

        .tabs-container--segmented .tab-button .tabs-tab-icon {
          margin-right: 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: #0a0a0a;
        }

        .tabs-container--segmented .tab-button .tabs-tab-icon svg {
          display: block;
        }

        .tabs-container--segmented .tab-button:hover:not(:disabled):not(.active) {
          color: #0a0a0a;
          background: rgba(255, 255, 255, 0.45);
        }

        .tabs-container--segmented .tab-button.active {
          color: #0a0a0a;
          font-weight: 600;
          background: #ffffff;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06);
        }

        .tabs-container--segmented .tab-button.active::after {
          display: none;
        }

        @media (max-width: 767px) {
          .tabs-container {
            flex-direction: column;
            gap: 0;
            border-bottom: none;
            padding-bottom: 0;
          }

          .tabs-container:not(.tabs-container--pill):not(.tabs-container--segmented) .tab-button {
            width: 100%;
            text-align: left;
            padding: 10px 12px;
          }

          .tabs-container:not(.tabs-container--pill):not(.tabs-container--segmented) .tab-button.active::after {
            display: none;
          }

          .tabs-container--pill {
            flex-direction: column;
            border-radius: 10px;
          }

          .tabs-container--pill .tab-button {
            flex: none;
            width: 100%;
            text-align: center;
            border-bottom: 1px solid #e2e8f0;
            padding: 12px 16px;
          }

          .tabs-container--pill .tab-button:last-child {
            border-bottom: none;
          }

          .tabs-container--pill .tab-button:first-child {
            border-radius: 10px 10px 0 0;
          }
          .tabs-container--pill .tab-button:last-child:not(:only-child) {
            border-radius: 0 0 10px 10px;
          }
          .tabs-container--pill .tab-button:only-child {
            border-radius: 10px;
          }

          .tabs-container--segmented {
            flex-direction: row;
            flex-wrap: nowrap;
            overflow-x: auto;
            width: fit-content;
            max-width: 100%;
          }

          .tabs-container--segmented .tab-button {
            flex: 0 0 auto;
            white-space: nowrap;
          }
        }
      `}</style>
    </>
  );
};

export default Tabs;
